import { FC, useEffect } from 'react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import ModalComponent from 'react-modal';
import type { Props as ReactModalProps, Styles } from 'react-modal';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';

// Define extended props interface
export interface ModalProps extends Omit<ReactModalProps, 'style'> {
  closeButton?: boolean;
  style?: {
    overlay?: React.CSSProperties;
    content?: React.CSSProperties;
  };
}

// Cast to FC to satisfy React 18's stricter JSX type checking.
// @types/react-modal uses class component types incompatible with React 18's JSX.ElementType.
// Props are still type-checked via ModalProps which extends ReactModalProps.
const ReactModal = ModalComponent as unknown as FC<ReactModalProps>;

/**
 * Modal overlay z-index. Set above the header (z-50) to ensure modals
 * appear on top of all page content including sticky navigation.
 */
const MODAL_OVERLAY_Z_INDEX = 51;

const Modal: FC<ModalProps> = ({ children, style, preventScroll, closeButton, className = '', ...props }) => {
  const [isDesktopSize] = useMediaQuery(desktop);

  const modalStyle: Styles = {
    overlay: {
      zIndex: MODAL_OVERLAY_Z_INDEX,
      backgroundColor: 'rgba(127, 127, 127, .3)',
      ...style?.overlay,
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
      maxWidth: isDesktopSize ? 800 : '100%',
      position: 'relative',
      padding: 0,
      ...style?.content,
    },
  };

  useEffect(() => {
    if (preventScroll) {
      document.body.style.overflow = props.isOpen ? 'hidden' : 'auto';
    }

    return () => {
      if (preventScroll) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [props.isOpen, preventScroll]);

  return (
    <ReactModal
      {...props}
      appElement={typeof document !== 'undefined' ? document.body : undefined}
      style={modalStyle}
      preventScroll={preventScroll}
      className={`${className} relative`}
      parentSelector={() => {
        // SSR safety: parentSelector is only called client-side by react-modal,
        // but this guard prevents errors if accidentally invoked during SSR
        if (typeof document === 'undefined') return null as unknown as HTMLElement;
        return document.getElementById('react-modal-portal') ?? document.body;
      }}
    >
      {closeButton && (
        <button
          type="button"
          onClick={props.onRequestClose}
          aria-label="Close modal"
          className="absolute right-20 top-20 z-50 size-24 cursor-pointer border-none bg-transparent p-0"
        >
          <CloseIcon className="size-24 text-icon-inverted" />
        </button>
      )}
      {children}
    </ReactModal>
  );
};

export default Modal;
